import { useCallback, useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ClipboardList, Download, Upload, Trash2, Users, CheckCircle2, Clock, AlertTriangle, Eye, BellRing } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const scaduto = (t) => t.scadenza && t.scadenza < today();

export default function TestPage() {
  const { user } = useAuth();
  const [tests, setTests] = useState([]);
  const [classi, setClassi] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ titolo: "", classe_id: "", scadenza: "", descrizione: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [consegne, setConsegne] = useState(null);
  const [valuta, setValuta] = useState(null);
  const canManage = user?.role === "admin" || user?.role === "docente";
  const token = () => localStorage.getItem("isteform_token");

  const load = useCallback(async () => {
    const reqs = [api.get("/test")];
    if (canManage) reqs.push(api.get("/classi"));
    const res = await Promise.all(reqs);
    setTests(res[0].data);
    if (canManage) setClassi(res[1].data);
  }, [canManage]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.titolo.trim() || !form.classe_id || !form.scadenza || !file) { toast.error("Compila titolo, classe, scadenza e allega il file"); return; }
    setSaving(true);
    const fd = new FormData();
    fd.append("titolo", form.titolo); fd.append("classe_id", form.classe_id);
    fd.append("scadenza", form.scadenza); fd.append("descrizione", form.descrizione); fd.append("file", file);
    try {
      const t = token();
      const r = await fetch(`${API}/test`, { method: "POST", body: fd, credentials: "include", headers: t ? { Authorization: `Bearer ${t}` } : {} });
      if (!r.ok) throw new Error();
      toast.success("Test creato e studenti notificati"); setOpen(false); setFile(null);
      setForm({ titolo: "", classe_id: "", scadenza: "", descrizione: "" }); load();
    } catch { toast.error("Errore creazione test"); }
    finally { setSaving(false); }
  };

  const dl = async (url, name) => {
    const t = token();
    const r = await fetch(`${API}${url}`, { credentials: "include", headers: t ? { Authorization: `Bearer ${t}` } : {} });
    if (!r.ok) { toast.error("Download non disponibile"); return; }
    const blob = await r.blob();
    const link = document.createElement("a"); link.href = URL.createObjectURL(blob); link.download = name; link.click();
  };

  const consegna = async (testId, fileObj) => {
    if (!fileObj) return;
    const fd = new FormData(); fd.append("file", fileObj);
    try {
      const t = token();
      const r = await fetch(`${API}/test/${testId}/consegna`, { method: "POST", body: fd, credentials: "include", headers: t ? { Authorization: `Bearer ${t}` } : {} });
      if (!r.ok) throw new Error();
      toast.success("Test consegnato"); load();
    } catch { toast.error("Errore consegna"); }
  };

  const del = async (id) => { if (!window.confirm("Eliminare il test?")) return; await api.delete(`/test/${id}`); toast.success("Eliminato"); load(); };

  const anteprima = async (url) => {
    const t = token();
    const r = await fetch(`${API}${url}`, { credentials: "include", headers: t ? { Authorization: `Bearer ${t}` } : {} });
    if (!r.ok) { toast.error("Anteprima non disponibile"); return; }
    const blob = await r.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };
  const sollecita = async (testId, studenteId) => {
    try { await api.post(`/test/${testId}/sollecita/${studenteId}`); toast.success("Sollecito inviato allo studente"); }
    catch { toast.error("Errore invio sollecito"); }
  };

  const openConsegne = async (t) => {
    try { const { data } = await api.get(`/test/${t.id}/consegne`); setConsegne(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const salvaValuta = async () => {
    try {
      await api.post(`/test/consegna/${valuta.consegna.id}/valuta`, { voto: valuta.voto === "" ? null : valuta.voto, esito: valuta.esito || "" });
      toast.success("Valutazione salvata"); const t = consegne.test; setValuta(null); openConsegne(t);
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="flex flex-wrap gap-4 items-center justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#F7941E] mb-2">Valutazioni</div>
          <h1 className="text-3xl font-bold text-stone-900">Area Test</h1>
          <p className="text-stone-600 mt-1">{canManage ? "Assegna test con scadenza; gli studenti ricevono la notifica e ricaricano il file compilato" : "Scarica il test, compilalo e ricaricalo entro la scadenza"}</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="new-test-btn"><ClipboardList className="w-4 h-4 mr-2"/>Nuovo Test</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nuovo Test</DialogTitle><DialogDescription>Allega il PDF del test, scegli la classe e la data di scadenza per la consegna.</DialogDescription></DialogHeader>
              <div className="grid gap-4">
                <div><Label>Titolo</Label><Input data-testid="test-titolo-input" value={form.titolo} onChange={e=>setForm({...form,titolo:e.target.value})}/></div>
                <div><Label>Classe destinataria</Label>
                  <Select value={form.classe_id} onValueChange={v=>setForm({...form,classe_id:v})}>
                    <SelectTrigger data-testid="test-classe-select"><SelectValue placeholder="Seleziona classe"/></SelectTrigger>
                    <SelectContent>{classi.map(c=><SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Scadenza consegna</Label><Input type="date" data-testid="test-scadenza-input" value={form.scadenza} onChange={e=>setForm({...form,scadenza:e.target.value})}/></div>
                <div><Label>Descrizione / istruzioni</Label><Textarea value={form.descrizione} onChange={e=>setForm({...form,descrizione:e.target.value})}/></div>
                <div><Label>File del test (PDF)</Label><Input type="file" accept="application/pdf" data-testid="test-file-input" onChange={e=>setFile(e.target.files[0])}/></div>
              </div>
              <DialogFooter><Button onClick={create} disabled={saving} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="test-save-btn">{saving?"Creazione...":"Crea e notifica"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tests.map(t => {
          const consegnato = !!t.mia_consegna;
          const overdue = scaduto(t);
          return (
            <Card key={t.id} className="p-5 bg-white border-[#E2E8F0]" data-testid={`test-card-${t.id}`}>
              <div className="flex justify-between items-start mb-2">
                <span className="font-bold text-stone-900 text-lg">{t.titolo}</span>
                {canManage && <Button variant="ghost" size="sm" onClick={()=>del(t.id)} data-testid={`del-test-${t.id}`}><Trash2 className="w-4 h-4 text-red-500"/></Button>}
              </div>
              <div className="text-xs text-stone-500 mb-1">Classe: {t.classe_nome}</div>
              <div className={`text-sm font-medium mb-2 flex items-center gap-1 ${overdue ? "text-red-600" : "text-stone-700"}`}>
                <Clock className="w-4 h-4"/> Scadenza: {t.scadenza}{overdue && " (scaduto)"}
              </div>
              {t.descrizione && <div className="text-sm text-stone-600 mb-3">{t.descrizione}</div>}

              <div className="flex flex-wrap gap-2 mt-2">
                <Button size="sm" variant="outline" onClick={()=>dl(`/test/${t.id}/download`, t.file?.original_filename || "test.pdf")} data-testid={`download-test-${t.id}`}><Download className="w-3 h-3 mr-1"/>Scarica test</Button>
                <Button size="sm" variant="ghost" onClick={()=>anteprima(`/test/${t.id}/download`)} data-testid={`anteprima-test-${t.id}`}><Eye className="w-3 h-3 mr-1"/>Anteprima</Button>
                {canManage && <Button size="sm" variant="outline" onClick={()=>openConsegne(t)} data-testid={`consegne-test-${t.id}`}><Users className="w-3 h-3 mr-1"/>Consegne {typeof t.consegne_count === "number" ? `(${t.consegne_count}/${t.studenti_count})` : ""}</Button>}
              </div>

              {user?.role === "studente" && (
                <div className="mt-3 pt-3 border-t border-[#E2E8F0]">
                  {consegnato ? (
                    <div className="text-sm" data-testid={`stato-consegna-${t.id}`}>
                      <span className="inline-flex items-center gap-1 text-emerald-700 font-medium"><CheckCircle2 className="w-4 h-4"/>Consegnato</span>
                      <div className="text-xs text-stone-500 mt-0.5">{t.mia_consegna.original_filename}</div>
                      {t.mia_consegna.esito && <div className="text-xs mt-1"><Badge className="bg-[#FFF5EB] text-[#C96F0E] border-[#F5D9B8]">Esito: {t.mia_consegna.esito}{t.mia_consegna.voto != null && t.mia_consegna.voto !== "" ? ` · ${t.mia_consegna.voto}` : ""}</Badge></div>}
                      <div className="mt-2"><Label className="text-xs">Sostituisci consegna</Label><Input type="file" onChange={e=>{if(e.target.files[0]){consegna(t.id, e.target.files[0]); e.target.value="";}}} data-testid={`ri-consegna-${t.id}`}/></div>
                    </div>
                  ) : overdue ? (
                    <div className="text-sm text-red-600 flex items-center gap-1" data-testid={`stato-consegna-${t.id}`}><AlertTriangle className="w-4 h-4"/>Scaduto senza consegna</div>
                  ) : (
                    <div>
                      <Label className="text-xs flex items-center gap-1"><Upload className="w-3 h-3"/>Carica il test compilato</Label>
                      <Input type="file" onChange={e=>{if(e.target.files[0]){consegna(t.id, e.target.files[0]); e.target.value="";}}} data-testid={`consegna-input-${t.id}`}/>
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
        {tests.length === 0 && <div className="col-span-full text-center text-stone-500 py-12">{canManage ? "Nessun test creato." : "Nessun test assegnato al momento."}</div>}
      </div>

      <Dialog open={!!consegne} onOpenChange={(o)=>{if(!o) setConsegne(null);}}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Consegne — {consegne?.test?.titolo}</DialogTitle><DialogDescription>Classe {consegne?.classe_nome}. Scarica i file consegnati e registra l'esito (opzionale).</DialogDescription></DialogHeader>
          {consegne && (
            <div className="space-y-2" data-testid="consegne-list">
              {consegne.consegne.map(c => (
                <div key={c.studente_id} className={`flex items-center gap-2 border rounded-lg p-3 ${c.consegna ? "border-[#E2E8F0]" : "border-red-200 bg-red-50"}`} data-testid={`consegna-row-${c.studente_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{c.studente_nome}</div>
                    {c.consegna ? (
                      <div className="text-xs text-stone-500">{c.consegna.original_filename} · {c.consegna.consegnato_at?.slice(0,16).replace("T"," ")}{c.consegna.esito ? ` · Esito: ${c.consegna.esito}${c.consegna.voto!=null&&c.consegna.voto!==""?` (${c.consegna.voto})`:""}` : ""}</div>
                    ) : <div className="text-xs text-red-600 font-semibold">In ritardo / non consegnato</div>}
                  </div>
                  {c.consegna && <Button size="sm" variant="ghost" onClick={()=>dl(`/test/consegna/${c.consegna.id}/download`, c.consegna.original_filename)} data-testid={`dl-consegna-${c.studente_id}`}><Download className="w-4 h-4 text-[#1F4E79]"/></Button>}
                  {c.consegna && <Button size="sm" variant="ghost" onClick={()=>anteprima(`/test/consegna/${c.consegna.id}/download`)} data-testid={`anteprima-consegna-${c.studente_id}`}><Eye className="w-4 h-4 text-[#1F4E79]"/></Button>}
                  {c.consegna && <Button size="sm" variant="outline" onClick={()=>setValuta({ consegna: c.consegna, voto: c.consegna.voto ?? "", esito: c.consegna.esito || "" })} data-testid={`valuta-${c.studente_id}`}>Valuta</Button>}
                  {!c.consegna && <Button size="sm" variant="outline" onClick={()=>sollecita(consegne.test.id, c.studente_id)} data-testid={`sollecita-${c.studente_id}`}><BellRing className="w-3 h-3 mr-1"/>Sollecita</Button>}
                </div>
              ))}
              {consegne.consegne.length === 0 && <div className="text-center text-sm text-stone-500 py-6">Nessuno studente nella classe.</div>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!valuta} onOpenChange={(o)=>{if(!o) setValuta(null);}}>
        <DialogContent>
          <DialogHeader><DialogTitle>Valuta consegna</DialogTitle><DialogDescription>Registra un esito e un voto (facoltativo). Lo studente riceverà una notifica.</DialogDescription></DialogHeader>
          {valuta && (
            <div className="grid gap-4">
              <div><Label>Esito</Label><Input data-testid="valuta-esito-input" value={valuta.esito} onChange={e=>setValuta({...valuta,esito:e.target.value})} placeholder="es. Superato / Da rivedere"/></div>
              <div><Label>Voto (facoltativo)</Label><Input data-testid="valuta-voto-input" value={valuta.voto} onChange={e=>setValuta({...valuta,voto:e.target.value})} placeholder="es. 28/30"/></div>
            </div>
          )}
          <DialogFooter><Button onClick={salvaValuta} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="valuta-save-btn">Salva valutazione</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
